import Filter from "../../../manager/filters/manager/filter"
import {FieldConditionsDropdown_NotAssignedOption, FieldConditionsDropdown_NotSetOption} from "../dropdown"
import FieldDropdown from "../../generic/dropdown/dropdown"
import ProjectStatusManager from "../../../gd/manager/project-status-manager"

// const debugGetOptions = "getFieldOptions"
const debugGetOptions = false

/**
 * Retrieves the options for a dropdown field, applying filtering logic if the number of options exceeds a threshold.
 * 
 * - Deep clones the field's options to avoid mutating the original array.
 * - If the number of options exceeds OPTIONS_THRESHOLD, it filters out options whose values are not present
 *   in the filter's values set by marking them as non-selectable.
 * 
 * @param field - The dropdown field whose options are to be retrieved.
 * @param filter - The filter instance used to collect the set of valid values.
 * @returns A Promise resolving to an array of Option objects, possibly with some marked as non-selectable.
 */
async function getFieldOptions(field: FieldDropdown, filter: Filter): Promise<Option[]> {
    const OPTIONS_THRESHOLD = 7

    // Get field options and create a deep clone to avoid mutations
    const fieldOptions: Option[] = _.compact(_.cloneDeep(field.getOptions())) as Option[]

    debugGetOptions && console.log(debugGetOptions, 'process', fieldOptions)

    // If there are more than the threshold number of options, filter them based on the filter's values set
    if (fieldOptions.length > OPTIONS_THRESHOLD) {
        // Collect the set of valid values for the filter
        const valuesSet = await filter.collectValuesSet()
        // If a values set exists, mark options as non-selectable if their value is not in the set
        if (valuesSet /*&& valuesSet.size > 0*/) {
            _.map(fieldOptions, (option: Option) => {
                // Mark options as non-selectable if their value is not in the valuesSet
                if (!valuesSet?.has(option.value)) {
                    option.excludeFromSelectable = true
                }
            })
        }
    }

    return fieldOptions
}

/**
 * Gets the options for a generic dropdown field filter, including the "Not Set" option if configured.
 * @param field - The dropdown field
 * @param filter - The filter instance
 * @returns A Promise resolving to an array of Option objects
 */
export async function FieldDropdown_filterGetOptions(field: FieldDropdown, filter: Filter) {
    // Get the filtered field options
    const fieldOptions = await getFieldOptions(field, filter)
    let options: Option[] = []

    // Add "Not Set" option if field is configured to show conditions not set option
    if (field.showConditionsNotSetOption) {
        options.push(FieldConditionsDropdown_NotSetOption)
    }

    return options.concat(fieldOptions)
}

/**
 * Gets the options for a user dropdown field filter, including the "Not Assigned" option if configured.
 * @param field - The dropdown field
 * @param filter - The filter instance
 * @returns A Promise resolving to an array of Option objects
 */
export async function FieldUser_filterGetOptions(field: FieldDropdown, filter: Filter) {
    // Get the filtered field options
    const fieldOptions = await getFieldOptions(field, filter)

    let options: Option[] = []

    // Add "not assigned" option if the field is configured to show it
    if (field.showConditionsNotSetOption) {
        options.push(FieldConditionsDropdown_NotAssignedOption)
    }

    return options.concat(fieldOptions)
}

/**
 * Gets the options for a project users dropdown field filter.
 * @param field - The dropdown field
 * @param filter - The filter instance
 * @returns A Promise resolving to an array of Option objects
 */
export async function FieldProjectUsers_filterGetOptions(field: FieldDropdown, filter: Filter) {
    // Get the filtered field options
    return await getFieldOptions(field, filter)
}

/**
 * Gets the options for a status dropdown field filter, filtering out options not present in the filter's values set.
 * @param field - The dropdown field
 * @param filter - The filter instance
 * @returns A Promise resolving to an array of Option objects
 */
export async function FieldStatus_filterGetOptions(field: FieldDropdown, filter: Filter) {
    // Get field options and create a deep clone to avoid mutations
    const fieldOptions: Option[] = _.compact(_.cloneDeep(gd.session.statuses.toOptions())) as Option[]

    // Get the set of values available for this filter
    const valuesSet = await filter.collectValuesSet()
    // Filter options based on valuesSet if it exists and has values
    if (valuesSet && valuesSet.size > 0) {
        _.map(fieldOptions, (option: Option) => {
            // Mark options as non-selectable if their value is not in the valuesSet
            if (!valuesSet?.has(option.value)) {
                option.excludeFromSelectable = true
            }
        })
    }

    return fieldOptions
}

/**
 * Gets the full list of project status options, including default statuses, and sorts them by system status.
 * @returns An array of Option objects representing project statuses
 */
export function FieldProjectStatus_getStatusesOptions() {
    // Get the current session's status options and clone them
    let res: Option[] = _.compact(_.cloneDeep(gd.session.statuses.toOptions())) as Option[]

    // Get the default statuses and add them to the result
    const defaultStatuses = ProjectStatusManager.getDefaultStatuses(undefined, {exportToOptions: true})
    res = res.concat(defaultStatuses)

    // Sort the options by their systemStatus property
    res = _.orderBy(res, (option: any) => {
        if (option.systemStatus) {
            return option.systemStatus
        } else {
            const status = gd.session.statuses.get(option.value)
            return status.systemStatus
        }
    })

    return res
}

/**
 * Gets the options for a project status dropdown field filter, filtering out options not present in the filter's values set.
 * @param field - The dropdown field
 * @param filter - The filter instance
 * @returns A Promise resolving to an array of Option objects
 */
export async function FieldProjectStatus_filterGetOptions(field: FieldDropdown, filter: Filter) {
    // Get the full list of project status options
    const fieldOptions: Option[] = FieldProjectStatus_getStatusesOptions()

    // Get the set of values available for this filter
    const valuesSet = await filter.collectValuesSet()
    // Filter options based on valuesSet if it exists and has values
    if (valuesSet && valuesSet.size > 0) {
        _.map(fieldOptions, (option: Option) => {
            // Mark options as non-selectable if their value is not in the valuesSet
            if (!valuesSet?.has(option.value)) {
                option.excludeFromSelectable = true
            }
        })
    }

    return fieldOptions
}

/**
 * Gets the options for a task users dropdown field filter.
 * @param field - The dropdown field
 * @param filter - The filter instance
 * @returns A Promise resolving to an array of Option objects
 */
export async function FieldTaskUsers_filterGetOptions(field: FieldDropdown, filter: Filter) {
    // Get the filtered field options
    return await getFieldOptions(field, filter)
}